/**
 * Luật thuần của danh mục model AI cấp nền tảng (ADR-0083). Không dựng chuỗi hiển thị: view tự dịch.
 */
import type { AiModel, AiModelUpdate, TenantAiModel } from '../types.ts';

/** Độ dài tối đa của `key` (`TenantAiModelsUpdate.disabled_keys[]` của OpenAPI). */
export const KHOA_TOI_DA = 60;

/** Bỏ dấu, `đ` thành `d`, chữ thường: so khớp tìm kiếm và sinh khoá dùng chung. */
export function khongDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();
}

/** Gốc khoá tối đa 55 ký tự để hậu tố `-n` vẫn vừa `KHOA_TOI_DA` (như `AiModelKey.GOC_TOI_DA` của tenant-service). */
const GOC_TOI_DA = 55;

/** Gốc khoá từ một chuỗi, cùng luật `AiModelKey.goc` của tenant-service; rỗng khi không đủ 2 ký tự hợp lệ. */
function gocKhoa(nguon: string): string {
  const goc = khongDau(nguon)
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, GOC_TOI_DA)
    .replace(/[-._]+$/, '');
  return goc.length >= 2 ? goc : '';
}

/**
 * Khoá dự kiến của model sắp tạo, đúng luật server (`AiModelKey` của tenant-service): lấy model id, không
 * sinh được thì lấy tên; bỏ dấu, chữ thường, giữ `.` `_` `-`, ký tự khác thành `-`; trùng khoá đã có thì
 * thêm `-2`, `-3`...
 *
 * Chỉ để **hiện trước** cho người dùng biết (ADR-UI-0014): server là chốt chặn và trả khoá thật.
 * Không sinh được (tên toàn ký hiệu) thì trả chuỗi rỗng để form báo lỗi ở ô tên.
 */
export function sinhKhoaModel(modelId: string, ten: string, daCo: Iterable<string> = []): string {
  const goc = gocKhoa(modelId.trim()) || gocKhoa(ten.trim());
  if (!goc) return '';
  const tap = new Set(daCo);
  if (!tap.has(goc)) return goc;
  for (let i = 2; ; i += 1) {
    const ung = `${goc}-${i}`;
    if (!tap.has(ung)) return ung;
  }
}

export type LocTrangThai = 'tat-ca' | 'bat' | 'tat';

/** Lọc danh mục ở client (vài chục dòng): engine, bật/tắt, từ khoá trên tên, khoá, model id. */
export function locModel(
  ds: readonly AiModel[],
  loc: { tuKhoa: string; engine?: string; trangThai: LocTrangThai },
): AiModel[] {
  const tim = khongDau(loc.tuKhoa.trim());
  return ds.filter(
    (m) =>
      (!loc.engine || m.engine === loc.engine) &&
      (loc.trangThai === 'tat-ca' || m.is_active === (loc.trangThai === 'bat')) &&
      (!tim || [m.label, m.key, m.model_id ?? ''].some((x) => khongDau(x).includes(tim))),
  );
}

/** Chỉ trường đã đổi so với bản gốc: `PATCH` rỗng thì không gọi server. */
export function truongDaDoi(goc: AiModelUpdate, moi: AiModelUpdate): AiModelUpdate {
  return Object.fromEntries(
    Object.entries(moi).filter(([k, v]) => goc[k as keyof AiModelUpdate] !== v),
  ) as AiModelUpdate;
}

/**
 * Model mặc định **của tenant** sau khi tắt bớt (ADR-0083 mục 4): model mặc định của nền tảng nếu
 * tenant dùng được, không thì model dùng được đầu tiên theo `display_order`. Hết model thì `null`.
 */
export function macDinhCuaTenant(dong: readonly TenantAiModel[], tat: ReadonlySet<string>): string | null {
  const dungDuoc = dong
    .filter((d) => !tat.has(d.model.key))
    .sort((a, b) => a.model.display_order - b.model.display_order);
  return (dungDuoc.find((d) => d.model.is_default) ?? dungDuoc[0])?.model.key ?? null;
}

/** Tập khoá đang tắt cho tenant theo dữ liệu server. */
export const tapDangTat = (dong: readonly TenantAiModel[]): Set<string> =>
  new Set(dong.filter((d) => !d.enabled).map((d) => d.model.key));

/**
 * Đọc hệ số token Fox người gõ (ADR-0123 mục 2): 0,001–1000, tối đa 3 chữ số thập phân, nhận dấu phẩy thập phân.
 * `null` khi sai — server cũng từ chối thay vì làm tròn, vì hệ số quyết số tiền.
 */
export function docHeSo(chuoi: string): number | null {
  const s = chuoi.trim().replace(',', '.');
  if (!/^\d{1,4}(\.\d{1,3})?$/.test(s)) return null;
  const v = Number(s);
  return v >= 0.001 && v <= 1000 ? v : null;
}

/**
 * Ô hệ số đọc cache: để trống là "bằng đầu vào" (`null`, ADR-0123 mục 2), không phải lỗi. `undefined` khi gõ sai.
 */
export function docHeSoCache(chuoi: string): number | null | undefined {
  if (chuoi.trim() === '') return null;
  return docHeSo(chuoi) ?? undefined;
}

/** Hai tập khoá có giống nhau không — để biết còn thay đổi chưa lưu. */
export function cungTap(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
